import React, { useState } from 'react';
import {
  Grid, Typography, Box, TextField, Button, Paper, Container, Chip, Alert, CircularProgress,
  Collapse,
} from '@mui/material';
import {
  Mail, ChatBubbleOutline, WhatsApp as WhatsAppIcon, ExpandMore as ExpandMoreIcon,
  ArrowOutward as ArrowOutwardIcon,
} from '@mui/icons-material';

const WEB3FORMS_KEY = '54e5d9bc-00a7-41c1-90c6-dfc9780a7761';
const SUPPORT_EMAIL = 'contact@cizlot.com';
const WHATSAPP_NUMBER = '916360513840'; // country code 91 + 6360513840
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  'Hi Cizlot team, I would like to know more about Cizlot.'
)}`;

const EMPTY_FORM = { name: '', phone: '', email: '', message: '' };

const validate = ({ name, phone, email, message }) => {
  const errors = {};

  if (name.trim().length < 2) errors.name = 'Please enter your name';
  else if (name.trim().length > 100) errors.name = 'Name is too long';

  const digits = phone.replace(/[^0-9]/g, '');
  const local = digits.startsWith('91') && digits.length === 12 ? digits.slice(2) : digits;
  if (!/^[6-9][0-9]{9}$/.test(local)) errors.phone = 'Enter a valid 10-digit mobile number';

  if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) {
    errors.email = 'Enter a valid email address';
  } else if (email.trim().length > 120) {
    errors.email = 'Email is too long';
  }

  if (message.trim().length < 5) errors.message = 'Please tell us a bit more';
  else if (message.trim().length > 1000) errors.message = 'Message is too long (max 1000)';

  return errors;
};

// Shared blue outlined style for every contact action button.
const CONTACT_BUTTON_SX = {
  py: 1.1,
  borderColor: 'rgba(32,133,216,0.4)',
  color: 'primary.main',
  '&:hover': { borderColor: 'primary.main', backgroundColor: 'rgba(32,133,216,0.06)' },
};

const ContactCard = ({ icon, accent, accentBg, title, description, detail, children }) => (
  <Paper
    elevation={0}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      p: { xs: 3, md: 3.5 },
      borderRadius: '24px',
      backgroundColor: '#ffffff',
      border: '1px solid rgba(17,24,39,0.07)',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
      '&:hover': {
        transform: 'translateY(-6px)',
        borderColor: 'rgba(32,133,216,0.25)',
        boxShadow: '0 22px 45px rgba(19,95,151,0.12)',
      },
    }}
  >
    <Box
      sx={{
        width: 56,
        height: 56,
        mb: 2.5,
        borderRadius: '16px',
        display: 'grid',
        placeItems: 'center',
        color: accent,
        backgroundColor: accentBg,
        '& svg': { fontSize: 28 },
      }}
    >
      {icon}
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
      {title}
    </Typography>
    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: detail ? 1.5 : 3 }}>
      {description}
    </Typography>
    {detail && (
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 3, wordBreak: 'break-word' }}>
        {detail}
      </Typography>
    )}
    <Box sx={{ mt: 'auto' }}>{children}</Box>
  </Paper>
);

const ContactSection = () => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    // honeypot — bots fill hidden fields, humans never do
    if (event.target.botcheck && event.target.botcheck.checked) return;

    const foundErrors = validate(form);
    if (Object.keys(foundErrors).length) {
      setErrors(foundErrors);
      setStatus(null);
      return;
    }

    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      message: form.message.trim(),
    };

    setLoading(true);
    setStatus(null);

    const sendEmail = fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        access_key: WEB3FORMS_KEY,
        subject: `New enquiry from ${payload.name} — Cizlot website`,
        from_name: 'Cizlot Website',
        replyto: payload.email || SUPPORT_EMAIL,
        ...payload,
      }),
    }).then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) throw new Error(data.message || 'Email failed');
      return true;
    });

    // Firebase is loaded on demand so it stays out of the initial JS bundle.
    const saveRecord = Promise.all([
      import('firebase/firestore'),
      import('../firebase'),
    ]).then(([{ addDoc, collection, serverTimestamp }, { db }]) =>
      addDoc(collection(db, 'contactEnquiries'), {
        ...payload,
        source: 'website',
        createdAt: serverTimestamp(),
      })
    );

    const [emailResult, recordResult] = await Promise.allSettled([sendEmail, saveRecord]);
    setLoading(false);

    if (emailResult.status === 'fulfilled' || recordResult.status === 'fulfilled') {
      setForm(EMPTY_FORM);
      setErrors({});
      setStatus({ type: 'success', message: 'Thanks for reaching out! Our team will contact you shortly.' });
    } else {
      setStatus({
        type: 'error',
        message: `Sorry, we could not send your message. Please email us at ${SUPPORT_EMAIL}.`,
      });
    }
  };

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, #e6f2fb 0%, #ffffff 100%)',
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
          <Chip
            label="Contact"
            sx={{ mb: 2, fontWeight: 600, color: '#135F97', backgroundColor: 'rgba(32,133,216,0.12)' }}
          />
          <Typography variant="h3" component="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, mb: 1.5 }}>
            Get in <span className="gradient-text">touch</span>
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 560, mx: 'auto' }}>
            Share your details and our team will get back to you.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <ContactCard
              icon={<ChatBubbleOutline />}
              accent="#2085D8"
              accentBg="rgba(32,133,216,0.12)"
              title="Send a message"
              description="Have a question or need help? Fill in a short form and our team will get back to you."
            >
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setShowForm((prev) => !prev)}
                aria-expanded={showForm}
                endIcon={
                  <ExpandMoreIcon
                    sx={{ transform: showForm ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s ease' }}
                  />
                }
                sx={CONTACT_BUTTON_SX}
              >
                {showForm ? 'Hide form' : 'Open form'}
              </Button>
            </ContactCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <ContactCard
              icon={<WhatsAppIcon />}
              accent="#25D366"
              accentBg="rgba(37,211,102,0.14)"
              title="WhatsApp"
              description="Prefer a quick chat? Message our team directly on WhatsApp."
              detail="+91 63605 13840"
            >
              <Button
                variant="outlined"
                fullWidth
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<ArrowOutwardIcon />}
                sx={CONTACT_BUTTON_SX}
              >
                Chat on WhatsApp
              </Button>
            </ContactCard>
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <ContactCard
              icon={<Mail />}
              accent="#2085D8"
              accentBg="rgba(32,133,216,0.12)"
              title="Email us"
              description="Write to us any time and we will reply to you by email."
              detail={SUPPORT_EMAIL}
            >
              <Button
                variant="outlined"
                fullWidth
                href={`mailto:${SUPPORT_EMAIL}`}
                endIcon={<ArrowOutwardIcon />}
                sx={CONTACT_BUTTON_SX}
              >
                Send Email
              </Button>
            </ContactCard>
          </Grid>
        </Grid>

        <Collapse in={showForm} timeout="auto" unmountOnExit>
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: { xs: 3, md: 4.5 },
              borderRadius: '22px',
              backgroundColor: '#ffffff',
              border: '1px solid rgba(17,24,39,0.07)',
              boxShadow: '0 22px 45px rgba(19,95,151,0.08)',
            }}
          >
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <input
                type="checkbox"
                name="botcheck"
                tabIndex={-1}
                autoComplete="off"
                style={{ display: 'none' }}
              />

              <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  name="name"
                  label="Name"
                  value={form.name}
                  onChange={handleChange}
                  error={Boolean(errors.name)}
                  helperText={errors.name}
                  disabled={loading}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  name="phone"
                  type="tel"
                  label="Contact Number"
                  placeholder="9876543210"
                  value={form.phone}
                  onChange={handleChange}
                  error={Boolean(errors.phone)}
                  helperText={errors.phone}
                  disabled={loading}
                  inputProps={{ maxLength: 15 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  label="Email (optional)"
                  value={form.email}
                  onChange={handleChange}
                  error={Boolean(errors.email)}
                  helperText={errors.email}
                  disabled={loading}
                  inputProps={{ maxLength: 120 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={4}
                  name="message"
                  label="Message"
                  value={form.message}
                  onChange={handleChange}
                  error={Boolean(errors.message)}
                  helperText={errors.message || `${form.message.length}/1000`}
                  disabled={loading}
                  inputProps={{ maxLength: 1000 }}
                />
              </Grid>

              {status && (
                <Grid item xs={12}>
                  <Alert severity={status.type}>{status.message}</Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={loading}
                  sx={{ py: 1.3, borderRadius: '14px' }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Send Message'}
                </Button>
              </Grid>
              </Grid>
            </Box>
          </Paper>
        </Collapse>
      </Container>
    </Box>
  );
};

export default ContactSection;
